import type { JSX } from 'react'

import { Sidebar, useMediaQuery, useSidebar } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import { ChannelsSection } from './components/ChannelsSection'
import { ChannelsSectionGroupSkeleton } from './components/ChannelsSection/ChannelsSectionSkeleton'
import {
  channelsSkeletonRows,
  useLastChannelsCount,
} from './components/ChannelsSection/useLastChannelsCount'
import { useChannels } from './components/ChannelsSection/useChannels'
import { GlobalButton } from './components/GlobalButton'
import { Logo, LogoIconWithStreakCount } from './components/Logo/Logo'
import { MainNavigation } from './components/MainNavigation'
import {
  MainSidebarFooter,
  MainSidebarFrame,
  MainSidebarHeader,
} from './components/MainSidebarFrame'
import { MobileSidebar } from './components/MobileSidebar'
import {
  OrganizationSwitcherProvider,
  useOrganizationSwitcherContext,
} from './components/OrganizationSwitcher/OrganizationSwitcherContext'
import { LockedChannelsSection } from './components/LockedChannelsSection'
import { SavedViewsSection } from './components/SavedViewsSection'
import { SecondChannelTourProvider } from './components/SecondChannelCelebration'
import { SidebarFooter } from './components/SidebarFooter'
import { ChannelLimitNotice } from '../PublishSidebar/ChannelLimitNotice/ChannelLimitNotice'
import { StreaksWidget } from './components/Streaks/StreaksWidget'

import styles from './MainSidebar.module.css'

/**
 * Small wrapper that accesses the org switcher context for loading state.
 * Must be rendered inside OrganizationSwitcherProvider.
 */
function ChannelsSectionWithSkeleton({
  channelsLoading,
}: {
  channelsLoading: boolean
}): JSX.Element {
  const { loading: accountLoading } = useAccount()
  const { loading: organizationSwitcherLoading } =
    useOrganizationSwitcherContext()
  const [lastChannelsCount] = useLastChannelsCount()

  const loading =
    channelsLoading || organizationSwitcherLoading || accountLoading

  if (loading) {
    return (
      <ChannelsSectionGroupSkeleton
        rows={channelsSkeletonRows(lastChannelsCount)}
      />
    )
  }

  return <ChannelsSection />
}

export function MainSidebar(): JSX.Element {
  const { open, state, setOpen } = useSidebar()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { channels, loading: channelsLoading } = useChannels()
  const { account } = useAccount()
  const isImpersonation = Boolean(account?.isImpersonation)

  // Render mobile sidebar on small screens
  if (isMobile) {
    return <MobileSidebar />
  }

  return (
    <OrganizationSwitcherProvider>
      <MainSidebarFrame open={open} onOpenChange={setOpen}>
        <SecondChannelTourProvider channels={channels}>
          <MainSidebarHeader
            logo={
              <>
                {state === 'expanded' && <Logo />}
                {state === 'collapsed' && <LogoIconWithStreakCount />}
                {state === 'expanded' && <StreaksWidget />}
              </>
            }
            globalButton={<GlobalButton />}
            navigation={<MainNavigation />}
          />
          <Sidebar.Content>
            <SavedViewsSection />
            <ChannelsSectionWithSkeleton channelsLoading={channelsLoading} />
            <LockedChannelsSection />
          </Sidebar.Content>
          <div className={styles.limitNoticeContainer}>
            <ChannelLimitNotice channels={channels} />
          </div>
          <MainSidebarFooter impersonation={isImpersonation}>
            <SidebarFooter />
          </MainSidebarFooter>
        </SecondChannelTourProvider>
      </MainSidebarFrame>
    </OrganizationSwitcherProvider>
  )
}
